import { Component, EventEmitter, HostListener, Input, OnChanges, OnDestroy, Output, SimpleChanges, ViewChild } from '@angular/core';
import { Question } from '../../interfaces/questionInterface';
import { FeedbackComponent } from '../feedback/feedback.component';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { faSolidCopy, faSolidEllipsis, faSolidShare } from '@ng-icons/font-awesome/solid';
import { faFlag } from '@ng-icons/font-awesome/regular';
import { ModalComponent } from '../../components/modal/modal.component';
import { FormsModule } from '@angular/forms';
import { NgClass } from '@angular/common';

@Component({
    selector: 'app-action-dropdown',
    imports: [FeedbackComponent, ModalComponent, NgIcon, FormsModule, NgClass],
    templateUrl: './action-dropdown.component.html',
    styleUrl: './action-dropdown.component.scss',
    providers: [provideIcons({ faSolidEllipsis, faFlag, faSolidShare, faSolidCopy })]
})
export class ActionDropdownComponent implements OnChanges, OnDestroy {
  @Input() question?: Question;
  @Input() bookmarkInfo?: { responseId: string, courseId?: string };
  @Input() bookmarked?: boolean;
  @Input() orderSeed?: string;

  isDropdownOpen: boolean = false;
  private documentClickListener: ((event: Event) => void) | null = null;

  questionShareLink: string = '';
  includeAnswer: boolean = false;
  includeOrderSeed: boolean = true;
  copied: boolean = false;
  updated: boolean = false;

  @Output()
  bookmarkedToggled = new EventEmitter<void>;

  @ViewChild(FeedbackComponent)
  feedbackComponent!: FeedbackComponent;

  @ViewChild('shareQuestionModal')
  shareQuestionModal!: ModalComponent;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['question']) {
      this.generateQuestionLink();
    }
  }

  toggleDropdown(event: Event) {
    event.stopPropagation();
    this.isDropdownOpen = !this.isDropdownOpen;

    if (this.isDropdownOpen) {
      this.addClickListener();
    } else {
      this.removeClickListener();
    }
  }

  private addClickListener() {
    this.documentClickListener = this.handleClickOutside.bind(this);
    document.addEventListener('click', this.documentClickListener);
  }

  private removeClickListener() {
    if (this.documentClickListener) {
      document.removeEventListener('click', this.documentClickListener);
      this.documentClickListener = null;
    }
  }

  private handleClickOutside(event: Event) {
    if (!(event.target as HTMLElement).closest('[data-dropdown-container]')) {
      this.isDropdownOpen = false;
      this.removeClickListener();
    }
  }

  ngOnDestroy() {
    this.removeClickListener();
  }

  copyToClipboard() {
    navigator.clipboard.writeText(this.questionShareLink);
    this.copied = true;
  }

  onChangeOptions() {
    this.generateQuestionLink();
    if (this.copied) {
      // user had the link copied already, update it
      this.copyToClipboard();
      this.updated = true;
    }
  }

  generateQuestionLink() {
    if (!this.question?.id) {
      this.questionShareLink = '';
      return;
    }

    const baseUrl = `${location.protocol}//${location.host}/question/${this.question.id}`;

    const params: string[] = [];

    if (this.includeAnswer) {
      params.push('show_answer=true');
    }

    if (this.includeOrderSeed && this.orderSeed) {
      params.push(`order=${encodeURIComponent(this.orderSeed)}`);
    }

    this.questionShareLink = params.length > 0
      ? `${baseUrl}?${params.join('&')}`
      : baseUrl;
  }

  close() {
    this.copied = false;
    this.updated = false;
    this.includeAnswer = false;
    this.generateQuestionLink();

    this.shareQuestionModal.toggleModal();
  }
}
